import { supabase } from "../../services/supabase";
import { shouldWriteRemote } from "../../services/supabaseEntityWrites";
import { logger, toLogError } from "../../utils/logger";

function sortNotesDesc(arr) {
  return [...(arr || [])].sort((a, b) =>
    String(b.createdAt || b.created_at || "").localeCompare(String(a.createdAt || a.created_at || ""))
  );
}

const NOTE_SELECT =
  "id, coach_id, client_id, type, content, rating, chips, source_session_id, created_at, updated_at";

export function mapClientNoteRow(row) {
  if (!row) return null;
  let chips = row.chips;
  if (chips == null) chips = [];
  else if (typeof chips === "string") {
    try {
      chips = JSON.parse(chips);
    } catch {
      chips = [];
    }
  }
  return {
    id: row.id,
    coachId: row.coach_id,
    clientId: row.client_id,
    type: row.type,
    content: row.content,
    rating: row.rating,
    chips: Array.isArray(chips) ? chips : [],
    sourceSessionId: row.source_session_id,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export function createClientNotesSlice(set, get) {
  return {
    clientNotes: [],
    clientNotesClientId: null,

    fetchClientNotes: async (clientId) => {
      if (!clientId) return;
      set({ clientNotesClientId: clientId });
      if (supabase && shouldWriteRemote(get().coachId)) {
        try {
          const { data, error } = await supabase
            .from("client_notes")
            .select(NOTE_SELECT)
            .eq("client_id", clientId)
            .order("created_at", { ascending: false })
            .limit(20);
          if (error) {
            logger.error("[fetchClientNotes]", toLogError(error), { clientId });
            set({ clientNotes: [] });
            return;
          }
          set({ clientNotes: (data || []).map(mapClientNoteRow).filter(Boolean) });
          return;
        } catch (err) {
          logger.error("[fetchClientNotes] network/table error", toLogError(err), { clientId });
          set({ clientNotes: [] });
          return;
        }
      }
      const c = get().clients.find((x) => x.id === clientId);
      const sorted = sortNotesDesc(c?.clientNotes);
      set({ clientNotes: sorted.slice(0, 10) });
    },

    addClientNote: async (clientId, { type, content, rating, chips, sourceSessionId } = {}) => {
      if (!content?.trim()) return;
      const trimmed = content.trim();
      if (supabase && shouldWriteRemote(get().coachId)) {
        const { data: userData } = await supabase.auth.getUser();
        const uid = userData?.user?.id;
        if (!uid) return;
        const { data, error } = await supabase
          .from("client_notes")
          .insert({
            coach_id: uid,
            client_id: clientId,
            type: type || "note",
            content: trimmed,
            rating: rating ?? null,
            chips: chips || [],
            source_session_id: sourceSessionId || null,
          })
          .select(NOTE_SELECT)
          .single();
        if (error) {
          logger.error("[addClientNote]", toLogError(error), { clientId });
          return;
        }
        const mapped = mapClientNoteRow(data);
        if (mapped) set({ clientNotes: [mapped, ...get().clientNotes] });
        return;
      }
      const note = {
        id: crypto.randomUUID(),
        coachId: get().coachId,
        clientId,
        type: type || "note",
        content: trimmed,
        rating: rating ?? null,
        chips: chips || [],
        sourceSessionId: sourceSessionId || null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      get().setClients((prev) =>
        prev.map((c) =>
          c.id === clientId ? { ...c, clientNotes: [...(c.clientNotes || []), note] } : c
        )
      );
      set({ clientNotes: [note, ...get().clientNotes] });
      get().persist?.();
    },

    loadMoreNotes: async (clientId) => {
      const { clientNotes } = get();
      if (!clientId || !clientNotes.length) return;
      if (supabase && shouldWriteRemote(get().coachId)) {
        const oldest = clientNotes[clientNotes.length - 1]?.createdAt;
        if (!oldest) return;
        const { data, error } = await supabase
          .from("client_notes")
          .select(NOTE_SELECT)
          .eq("client_id", clientId)
          .lt("created_at", oldest)
          .order("created_at", { ascending: false })
          .limit(10);
        if (error) {
          logger.error("[loadMoreNotes]", toLogError(error), { clientId });
          return;
        }
        const mapped = (data || []).map(mapClientNoteRow).filter(Boolean);
        if (mapped.length) set({ clientNotes: [...clientNotes, ...mapped] });
        return;
      }
      const c = get().clients.find((x) => x.id === clientId);
      const all = sortNotesDesc(c?.clientNotes);
      if (clientNotes.length >= all.length) return;
      const next = all.slice(clientNotes.length, clientNotes.length + 10);
      if (next.length) set({ clientNotes: [...clientNotes, ...next] });
    },
  };
}
